// Faithful local reimplementations of reactflow's `addEdge`, `applyNodeChanges`
// and `applyEdgeChanges` pure helpers, copied verbatim from @reactflow/core v11.
//
// Why: ChatbotReducer (a redux slice registered in BOTH the content-script
// extension-store and the website web-store) previously did
// `import { addEdge, applyEdgeChanges, applyNodeChanges } from 'reactflow'`.
// reactflow's ESM entry is a single pre-bundled `.mjs` that webpack cannot
// tree-shake, so importing these three tiny array helpers dragged the ENTIRE
// reactflow + @reactflow/core renderer (~391KB) into the content-script
// `app.js`. The flow-canvas UI (bt-chatbot-view / chatbot-flow / bt-nodes) is
// website/dashboard-only and never runs in the content script — the content
// store only needs these three pure functions.
//
// Reimplementing them locally (types stay `import type`, fully erased) severs
// the runtime barrel edge so reactflow drops out of app.js entirely, while the
// website keeps identical behaviour. See PERF_AUDIT_PROGRESS.md "R17-B".
import type { Connection, Edge, EdgeChange, Node, NodeChange } from 'reactflow';

const isEdge = (element: any): boolean =>
    'id' in element && 'source' in element && 'target' in element;

const getEdgeId = ({ source, sourceHandle, target, targetHandle }: any): string =>
    `reactflow__edge-${source}${sourceHandle || ''}-${target}${targetHandle || ''}`;

const connectionExists = (edge: any, edges: any[]): boolean =>
    edges.some(
        (el) =>
            el.source === edge.source &&
            el.target === edge.target &&
            (el.sourceHandle === edge.sourceHandle || (!el.sourceHandle && !edge.sourceHandle)) &&
            (el.targetHandle === edge.targetHandle || (!el.targetHandle && !edge.targetHandle))
    );

export const addEdge = (edgeParams: Edge | Connection, edges: Edge[]): Edge[] => {
    if (!edgeParams.source || !edgeParams.target) {
        return edges;
    }
    let edge: Edge;
    if (isEdge(edgeParams)) {
        edge = { ...(edgeParams as Edge) };
    } else {
        edge = { ...edgeParams, id: getEdgeId(edgeParams) } as Edge;
    }
    if (connectionExists(edge, edges)) {
        return edges;
    }
    return edges.concat(edge);
};

function handleParentExpand(res: any[], updateItem: any): void {
    const parentId = updateItem.parentNode || updateItem.parentId;
    const parent = res.find((e) => e.id === parentId);
    if (parent) {
        const extendWidth = updateItem.position.x + updateItem.width - parent.width;
        const extendHeight = updateItem.position.y + updateItem.height - parent.height;
        if (extendWidth > 0 || extendHeight > 0 || updateItem.position.x < 0 || updateItem.position.y < 0) {
            // (reactflow's original wrote `{ ...x } || {}`; the `|| {}` is dead
            // since object spread is always truthy — dropped to satisfy tsc.)
            parent.style = { ...parent.style };
            parent.style.width = parent.style.width ?? parent.width;
            parent.style.height = parent.style.height ?? parent.height;
            if (extendWidth > 0) {
                parent.style.width += extendWidth;
            }
            if (extendHeight > 0) {
                parent.style.height += extendHeight;
            }
            if (updateItem.position.x < 0) {
                const xDiff = Math.abs(updateItem.position.x);
                parent.position.x = parent.position.x - xDiff;
                parent.style.width += xDiff;
                updateItem.position.x = 0;
            }
            if (updateItem.position.y < 0) {
                const yDiff = Math.abs(updateItem.position.y);
                parent.position.y = parent.position.y - yDiff;
                parent.style.height += yDiff;
                updateItem.position.y = 0;
            }
            parent.width = parent.style.width;
            parent.height = parent.style.height;
        }
    }
}

function applyChanges(changes: any[], elements: any[]): any[] {
    // Handle the setNodes/setEdges reset path of controlled flows.
    if (changes.some((c) => c.type === 'reset')) {
        return changes.filter((c) => c.type === 'reset').map((c) => c.item);
    }
    const initElements = changes.filter((c) => c.type === 'add').map((c) => c.item);
    return elements.reduce((res, item) => {
        const currentChanges = changes.filter((c) => c.id === item.id);
        if (currentChanges.length === 0) {
            res.push(item);
            return res;
        }
        const updateItem = { ...item };
        for (const currentChange of currentChanges) {
            if (currentChange) {
                switch (currentChange.type) {
                    case 'select': {
                        updateItem.selected = currentChange.selected;
                        break;
                    }
                    case 'position': {
                        if (typeof currentChange.position !== 'undefined') {
                            updateItem.position = currentChange.position;
                        }
                        if (typeof currentChange.positionAbsolute !== 'undefined') {
                            updateItem.positionAbsolute = currentChange.positionAbsolute;
                        }
                        if (typeof currentChange.dragging !== 'undefined') {
                            updateItem.dragging = currentChange.dragging;
                        }
                        if (updateItem.expandParent) {
                            handleParentExpand(res, updateItem);
                        }
                        break;
                    }
                    case 'dimensions': {
                        if (typeof currentChange.dimensions !== 'undefined') {
                            updateItem.width = currentChange.dimensions.width;
                            updateItem.height = currentChange.dimensions.height;
                        }
                        if (typeof currentChange.updateStyle !== 'undefined') {
                            updateItem.style = { ...(updateItem.style || {}), ...currentChange.dimensions };
                        }
                        if (typeof currentChange.resizing === 'boolean') {
                            updateItem.resizing = currentChange.resizing;
                        }
                        if (updateItem.expandParent) {
                            handleParentExpand(res, updateItem);
                        }
                        break;
                    }
                    case 'remove': {
                        return res;
                    }
                }
            }
        }
        res.push(updateItem);
        return res;
    }, initElements);
}

export function applyNodeChanges(changes: NodeChange[], nodes: Node[]): Node[] {
    return applyChanges(changes, nodes) as Node[];
}

export function applyEdgeChanges(changes: EdgeChange[], edges: Edge[]): Edge[] {
    return applyChanges(changes, edges) as Edge[];
}
